import React from "react";
import {
  Card,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import moment from "moment";
import { CopyableId } from "../../../utils/SensorNameMappings";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    overflow: "hidden",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: theme.spacing(3),
    background: "linear-gradient(180deg, #e8f4f8 0%, #d9eef3 100%)",
    borderBottom: "4px solid #0076bb",
  },
  title: {
    color: "#0076bb",
    fontWeight: 700,
    lineHeight: 1,
  },
  subtitle: {
    marginTop: theme.spacing(0.75),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  chip: {
    backgroundColor: "#fff",
    fontWeight: 600,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  successChip: {
    color: "#2e7d32",
    borderColor: "#2e7d32",
  },
  errorChip: {
    color: "#d32f2f",
    borderColor: "#d32f2f",
  },
  primaryChip: {
    color: "#0076bb",
    borderColor: "#0076bb",
  },
  lastUpdatedContainer: {
    padding: theme.spacing(2, 3),
    backgroundColor: "#d9eef3",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  lastUpdated: {
    color: "#009444",
    fontWeight: 600,
  },
  content: {
    padding: theme.spacing(3),
    flex: 1,
  },
  groupTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0076bb",
    fontWeight: 700,
    fontSize: 16,
    textTransform: "uppercase",
    letterSpacing: 2,
    margin: theme.spacing(3, 0),

    "&::before, &::after": {
      content: '""',
      flex: 1,
      borderBottom: "2px solid #d9eef3",
    },

    "&::before": {
      marginRight: theme.spacing(2),
    },

    "&::after": {
      marginLeft: theme.spacing(2),
    },
  },
  sectionTitle: {
    color: "#0076bb",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    backgroundColor: "#fafafa",
  },
  detailLabel: {
    color: "#0076bb",
    fontWeight: 500,
  },
  copyButton: {
    color: "#0076bb",
    "&:hover": {
      backgroundColor: "#d9eef3",
    },
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
}));

/* -------------------- ROW -------------------- */

function DetailRow({ heading, label, copyValue }) {
  const classes = useStyles();

  const handleCopy = async () => {
    if (!copyValue) return;
    await navigator.clipboard.writeText(String(copyValue));
  };

  return (
    <Box className={classes.detailRow}>
      <Typography variant="body2" className={classes.detailLabel}>
        {label}
      </Typography>

      {copyValue && (
        <Tooltip title={`Copy ${heading}`}>
          <IconButton
            size="small"
            onClick={handleCopy}
            className={classes.copyButton}
          >
            <FileCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const formatLastReportedOn = (value) => {
  if (!value) {
    return "N/A";
  }
  return moment(new Date(value)).format("MM/DD/YYYY hh:mm A");
};

function SectionTitle({ children }) {
  const classes = useStyles();
  return <Typography className={classes.sectionTitle}>{children}</Typography>;
}
function GroupTitle({ children }) {
  const classes = useStyles();
  return <Typography className={classes.groupTitle}>{children}</Typography>;
}

const excludedKeysSensor = [
  "longitude",
  "latitude",
  "organizationID",
  "isActive",
  "isHmacEnabled",
];

const readingContextConfig = {
  is_binary: "Binary Attribute",
  is_reported: "Reporting Enabled",
  is_cumulative: "Cumulative Reading",
  is_reset_daily: "Daily Reset",
  daily_resets_at: "Daily Reset Time",
  daily_reset_timezone: "Reset Timezone",
  count_object: "Count Object",
  report_algorithm: "Reporting Algorithm",
  type: "Reading Type",
};
const excludedKeysAssociation = ["is_advance", "is_deleted"];

export default function AttributeDetailsCard({ data }) {
  const classes = useStyles();

  const sensor = data?.details?.sensor;
  const reading = data?.details?.reading ?? [];
  const association = data?.details?.association;

  if (!sensor) return null;

  return (
    <Card elevation={6} className={classes.card}>
      <Box className={classes.header}>
        <Box>
          <h1 className={classes.title}>{reading.last_value}</h1>
          <h5 className={classes.title}>{reading.attribute_name}</h5>

          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.subtitle}
          >
            <CopyableId label="ID" id={reading.id} icon />
          </Typography>
        </Box>
      </Box>

      <Box className={classes.lastUpdatedContainer}>
        <Typography variant="body2" className={classes.lastUpdated}>
          Last Reported: {reading.reportDateEST}
        </Typography>
      </Box>

      <Box className={classes.content}>
        {" "}
        {reading?.context != null && (
          <>
            <GroupTitle>Reading Information</GroupTitle>

            {Object.entries(readingContextConfig)
              .filter(([key]) => key in reading.context)
              .map(([key, label], index, filteredArray) => {
                const value = reading.context[key];
                let display;

                if (typeof value === "boolean") {
                  display = value ? "Enabled" : "Disabled";
                } else {
                  display = value;
                }

                return (
                  <Box key={key}>
                    <SectionTitle>{label}</SectionTitle>

                    <DetailRow
                      heading={label.toUpperCase()}
                      label={display}
                      copyValue={display}
                    />

                    {index !== filteredArray.length - 1 && (
                      <Divider className={classes.divider} />
                    )}
                  </Box>
                );
              })}
          </>
        )}
        {association?.type != null && (
          <>
            <GroupTitle>Association</GroupTitle>

            {Object.entries(association.facility)
              .filter(([key]) => !excludedKeysAssociation.includes(key))
              .map(([key, value], index) => {
                let display =
                  key === "status" ? (value ? "Active" : "Inactive") : value;

                return (
                  <Box key={key}>
                    <SectionTitle>{`${key.replace(/_/g, " ")}`}</SectionTitle>
                    <DetailRow
                      heading={key.toUpperCase()}
                      label={display}
                      copyValue={display}
                    />

                    {index !==
                      Math.min(Object.keys(association).length, 5) - 1 && (
                      <Divider className={classes.divider} />
                    )}
                  </Box>
                );
              })}
          </>
        )}
        {sensor?.facility_id != null && (
          <>
            <GroupTitle>Sensor Information</GroupTitle>

            {Object.entries(sensor)
              .filter(([key]) => !excludedKeysSensor.includes(key))
              .map(([key, value], index, filteredArray) => {
                let display =
                  key === "status" ? (value ? "Active" : "Inactive") : value;

                return (
                  <Box key={key}>
                    <SectionTitle>{key.replace(/_/g, " ")}</SectionTitle>

                    <DetailRow
                      heading={key.toUpperCase()}
                      label={display}
                      copyValue={display}
                    />

                    {index !== filteredArray.length - 1 && (
                      <Divider className={classes.divider} />
                    )}
                  </Box>
                );
              })}
          </>
        )}
      </Box>
    </Card>
  );
}
